import { REGEXP_ONLY_DIGITS } from "input-otp";
import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { cn } from "@/shared/lib/utils";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "../primitives/input-otp";

export interface ColocaOtpFieldProps {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the OTP slots. */
  label?: ReactNode;
  /** Helper text rendered below the input. */
  description?: ReactNode;
  /** Total number of slots. Defaults to 6. Should be even for the centered separator. */
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Called when the user fills every slot. Use for auto-submit. */
  onComplete?: (code: string) => void;
  className?: string;
  /** Tailwind classes for each slot — defaults to a larger size than the shadcn primitive. */
  slotClassName?: string;
}

/**
 * Controlled one-time-password input wired to the nearest React Hook Form context.
 *
 * Renders two groups of `length / 2` digit slots separated by a horizontal divider. Restricts input to digits
 * (`REGEXP_ONLY_DIGITS`) and surfaces Zod validation errors automatically via `<FormMessage>`.
 */
export function ColocaOtpField({
  name,
  label,
  description,
  length = 6,
  disabled,
  autoFocus,
  onComplete,
  className,
  slotClassName,
}: ColocaOtpFieldProps) {
  const { control } = useFormContext();
  const half = Math.floor(length / 2);
  const firstGroupSlots = Array.from({ length: half }, (_, i) => i);
  const secondGroupSlots = Array.from({ length: length - half }, (_, i) => half + i);
  const slotClass = cn("sm:w-12 sm:h-12 sm:text-base", slotClassName);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("justify-items-center", className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <InputOTP
              maxLength={length}
              pattern={REGEXP_ONLY_DIGITS}
              value={field.value ?? ""}
              onChange={(value: string) => {
                field.onChange(value);
                if (value.length === length) {
                  onComplete?.(value);
                }
              }}
              onBlur={field.onBlur}
              disabled={disabled}
              autoFocus={autoFocus}
              containerClassName="justify-center"
            >
              <InputOTPGroup>
                {firstGroupSlots.map((index) => (
                  <InputOTPSlot key={index} index={index} className={slotClass} />
                ))}
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                {secondGroupSlots.map((index) => (
                  <InputOTPSlot key={index} index={index} className={slotClass} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
